/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Daily usage (Marketers & business teams)", description = "Learn where marketers work in Hightouch and how to build audiences, run campaigns, and measure results.", lastUpdatedDate = new Date(1787616000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Daily usage (Marketers & business teams)",
        description: "Learn where marketers work in Hightouch and how to build audiences, run campaigns, and measure results.",
        lastUpdatedDate: "Tue Aug 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Before you start",
          "depth": 0,
          "id": "before-you-start"
        }, {
          "value": "What you can do yourself vs. what requires setup",
          "depth": 0,
          "id": "what-you-can-do-yourself-vs-what-requires-setup"
        }, {
          "value": "Choose your starting point",
          "depth": 0,
          "id": "choose-your-starting-point"
        }, {
          "value": "Core workflow",
          "depth": 0,
          "id": "core-workflow"
        }, {
          "value": "1. Explore the data available to you",
          "depth": 1,
          "id": "1-explore-the-data-available-to-you"
        }, {
          "value": "2. Build an audience",
          "depth": 1,
          "id": "2-build-an-audience"
        }, {
          "value": "3. Activate your audience",
          "depth": 1,
          "id": "3-activate-your-audience"
        }, {
          "value": "4. Launch with guardrails",
          "depth": 1,
          "id": "4-launch-with-guardrails"
        }, {
          "value": "5. Monitor results and iterate",
          "depth": 1,
          "id": "5-monitor-results-and-iterate"
        }, {
          "value": "Related products",
          "depth": 0,
          "id": "related-products"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["Access to the correct Hightouch workspace or ", _jsx(_components.a, {
                    href: "/workspace-management/spaces",
                    children: "Space"
                  })]
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  }), " configured by your data team"]
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/destinations/overview",
                    children: "destination"
                  }), " available for activation"]
                }), _jsx("li", {
                  children: "A role with permission to create audiences or launch campaigns"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Hightouch lets marketers work directly with governed warehouse data. Your platform admin and data team set up access, schema, and guardrails first. After that, you can build audiences, run campaigns, and measure results within those boundaries."
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Platform admins and data teams set up access, schema, and governance before\nmarketers start working. This guide focuses on what marketers do after that\nsetup is complete. See ", _jsx(_components.a, {
            href: "/getting-started/it",
            children: "Workspace setup"
          }), " and ", _jsx(_components.a, {
            href: "/getting-started/data",
            children: "Technical\nsetup"
          }), " for setup details."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://architecture.hightouch.com/?from=docs&product=overview&utm_source=hightouch-docs&utm_medium=referral&utm_campaign=architecture-explorer&utm_content=overview",
            children: _jsx(_components.strong, {
              children: "Explore the interactive architecture diagram"
            })
          }), " — see how data flows and where each Hightouch product fits in your marketing stack."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "before-you-start",
        children: "Before you start"
      }), "\n", _jsx(_components.p, {
        children: "If something looks missing after you log in, it may be expected governance rather than a bug."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can't see certain audiences or syncs?"
          }), " Your platform admin may use ", _jsx(_components.a, {
            href: "/workspace-management/spaces",
            children: "Spaces"
          }), " to limit which resources are visible to each team, organized by region, brand, or business unit."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Missing customer attributes or events?"
          }), " Ask your data team. They define which data appears in the audience builder through the ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "schema"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can't access a destination?"
          }), " Platform admins control which marketing tools are connected and may use ", _jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "destination rules"
          }), " to restrict which records can be sent to specific channels, or ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "approval flows"
          }), " to require review before changes go live."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-you-can-do-yourself-vs-what-requires-setup",
        children: "What you can do yourself vs. what requires setup"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "You can usually do this yourself"
            }), _jsx(_components.th, {
              children: "Your platform admin sets this up"
            }), _jsx(_components.th, {
              children: "Your data team sets this up"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build and save audiences"
            }), _jsx(_components.td, {
              children: "Create workspaces and manage access (roles, SSO, groups)"
            }), _jsx(_components.td, {
              children: "Connect sources and configure sync engines"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Preview and validate audiences"
            }), _jsx(_components.td, {
              children: "Set governance guardrails (subsets, destination rules, approvals)"
            }), _jsx(_components.td, {
              children: "Define schema, models, and traits"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reuse audience or sync templates"
            }), _jsx(_components.td, {
              children: "Create templates for audiences and syncs"
            }), _jsx(_components.td, {
              children: "Expose new fields when you need them"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Launch syncs or journeys within your permissions"
            }), _jsx(_components.td, {
              children: "Connect destinations (either team)"
            }), _jsx(_components.td, {
              children: "Connect destinations (either team)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Review sync results and campaign performance"
            }), _jsx(_components.td, {}), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-your-starting-point",
        children: "Choose your starting point"
      }), "\n", _jsx(_components.p, {
        children: "What you do first depends on your goal."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If you want to..."
            }), _jsx(_components.th, {
              children: "Start here"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Explore customer data or campaign performance in plain English"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/agents/overview",
                children: "Agents overview"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build an audience and send it to a marketing tool"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/usage",
                children: "Build an audience"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Run a multi-step workflow with branching logic"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/journeys",
                children: "Journeys"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Measure campaign lift or compare audience performance"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/campaign-intelligence/overview",
                children: "Intelligence overview"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Let AI choose the best channel, message, and timing"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/overview",
                children: "AI Decisioning overview"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Lifecycle Studio"
          }), " and ", _jsx(_components.strong, {
            children: "Ad Studio"
          }), " are two additional product areas for\nmarketers. Documentation for both is coming soon."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-workflow",
        children: "Core workflow"
      }), "\n", _jsx(_components.p, {
        children: "Most marketer workflows in Hightouch follow the same pattern: explore data, build an audience, activate it, and measure results."
      }), "\n", _jsx(_components.h3, {
        id: "1-explore-the-data-available-to-you",
        children: "1. Explore the data available to you"
      }), "\n", _jsxs(_components.p, {
        children: ["Start by understanding what you're building with. Your data team defines a ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "schema"
        }), " that determines which customer attributes, related models, and events appear in the audience builder."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "parent model"
          }), " is your main dataset — typically a ", _jsx(_components.code, {
            children: "users"
          }), " or ", _jsx(_components.code, {
            children: "customers"
          }), " table."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Related models"
          }), " add supporting context like accounts, subscriptions, or product catalogs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events"
          }), " are timestamped actions like purchases, logins, or cancellations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/traits",
            children: _jsx(_components.strong, {
              children: "Traits"
            })
          }), " are computed values (aggregations, most-recent values, or custom formulas) defined by your data team. You use them as filters when building audiences."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Your platform admin may have configured ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), " that limit which records you can query, for example by region or brand. If you're only seeing a portion of the data, you're likely seeing the data relevant to your team."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you'd rather ask questions than click through filters, use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " to explore customer data and campaign performance in natural language. Instead of writing SQL or filing a ticket with your data team, describe what you want to know and the agent returns answers grounded in the same warehouse data that powers your audiences. You can save prompts as recurring reports that run on a schedule."]
      }), "\n", _jsx(_components.h3, {
        id: "2-build-an-audience",
        children: "2. Build an audience"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "Customer Studio"
        }), " to create audiences from the data available in your schema."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Choose a parent model."
        }), "\n", _jsx(_components.li, {
          children: "Add filters using customer attributes, related data, or events."
        }), "\n", _jsx(_components.li, {
          children: "Preview the audience to verify the results look right."
        }), "\n", _jsx(_components.li, {
          children: "Save the audience."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If your team uses ", _jsx(_components.a, {
            href: "/customer-studio/templates",
            children: "audience templates"
          }), ", start\nthere. Templates reduce setup time and help enforce consistent campaign logic\nacross teams."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "3-activate-your-audience",
        children: "3. Activate your audience"
      }), "\n", _jsx(_components.p, {
        children: "Once you've built an audience, decide how to get it into your downstream tools."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync it directly"
          }), " to a destination your data team has already connected (ad platforms, ESPs, CRMs) using ", _jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "audience syncs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build a multi-step flow"
          }), " with ", _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "Journeys"
          }), ", which support branching, delays, and conditional logic. For example: a customer abandons checkout → wait 2 hours → send a reminder email → if no purchase after 24 hours → send an SMS with a discount code. Journeys evaluate on a schedule, so members enter, advance, exit, and sync when the journey runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Optimize delivery with AI"
          }), " using ", _jsx(_components.a, {
            href: "/ai-decisioning/overview",
            children: "AI Decisioning"
          }), ", which uses reinforcement learning to pick the best channel, message, and timing for each customer. You define the goal and AI Decisioning learns what works over time."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "4-launch-with-guardrails",
        children: "4. Launch with guardrails"
      }), "\n", _jsx(_components.p, {
        children: "Depending on how your workspace is configured, some controls may apply automatically when you activate an audience."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: _jsx(_components.strong, {
              children: "Approval flows"
            })
          }), " may require a teammate or admin to review changes before they go live."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: _jsx(_components.strong, {
              children: "Destination rules"
            })
          }), " may automatically exclude records that don't meet channel or consent requirements."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: _jsx(_components.strong, {
              children: "Subsets"
            })
          }), " may limit which records you can include in an audience based on region, brand, or compliance boundaries."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "These guardrails are configured by your platform admin or data team. They're designed to let you move quickly without bypassing data controls."
      }), "\n", _jsx(_components.h3, {
        id: "5-monitor-results-and-iterate",
        children: "5. Monitor results and iterate"
      }), "\n", _jsx(_components.p, {
        children: "After launch, check how your audiences and campaigns are performing."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Review ", _jsx(_components.strong, {
            children: "sync status"
          }), " from the sync detail page to see whether records were delivered, rejected, or errored."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/campaign-intelligence/overview",
            children: _jsx(_components.strong, {
              children: "Intelligence"
            })
          }), " to measure results. ", _jsx(_components.strong, {
            children: "Splits"
          }), " divide audiences into test and control groups to measure incremental impact. ", _jsx(_components.strong, {
            children: "Holdouts"
          }), " withhold a portion of an audience to establish a baseline. ", _jsx(_components.strong, {
            children: "Dashboards"
          }), " track campaign metrics over time and let you drill into performance by audience, treatment, or trait. Results write back to your warehouse, so your data team can include them in broader reporting."]
        }), "\n", _jsxs(_components.li, {
          children: ["Run ", _jsx(_components.a, {
            href: "/customer-studio/experiments",
            children: _jsx(_components.strong, {
              children: "Experiments"
            })
          }), " to test audience variants and measure which performs better."]
        }), "\n", _jsxs(_components.li, {
          children: ["Ask an ", _jsx(_components.a, {
            href: "/agents/overview",
            children: _jsx(_components.strong, {
              children: "Agent"
            })
          }), " to analyze campaign performance in natural language — compare segments, spot trends, or generate a recurring report without writing SQL."]
        }), "\n", _jsx(_components.li, {
          children: "Revisit your audience logic based on performance data, approval feedback, or error patterns."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "related-products",
        children: "Related products"
      }), "\n", _jsx(_components.p, {
        children: "These products work alongside Customer Studio and may already be set up in your workspace. Ask your admin if you're unsure what's available."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/platform/identity-resolution",
            children: _jsx(_components.strong, {
              children: "Identity Resolution"
            })
          }), ": Unified customer profiles across devices and channels."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/events/overview",
            children: _jsx(_components.strong, {
              children: "Events"
            })
          }), ": Campaign triggers based on customer actions, either as scheduled batches or in real time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/match-booster/overview",
            children: _jsx(_components.strong, {
              children: "Match Booster"
            })
          }), ": Improved ad platform match rates through identifier enrichment."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/real-time/overview",
            children: _jsx(_components.strong, {
              children: "Real-Time Personalization"
            })
          }), ": In-session experiences that respond to what a customer is doing right now."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For a deeper walkthrough of the marketing workflow, see the ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio overview"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
